// src/pages/onboarding/OnboardingBookingPage.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OnboardingLayout from "./OnboardingLayout";
import {
  getBotById,
  updateBot,
  BookingWeeklySchedule,
  Weekday
} from "../../api/bots";

import { ALWAYS_ON_FEATURES } from "./onboardingFeatureHelpers";

const WEEKDAYS_ORDER: Weekday[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];

interface OpeningWindowForm {
  start: string;
  end: string;
}

interface DayScheduleForm {
  enabled: boolean;
  windows: OpeningWindowForm[];
}

type WeeklyScheduleForm = Record<Weekday, DayScheduleForm>;

interface ServiceForm {
  name: string;
  aliases: string;
  calendarId: string;
  durationMinutes: string;
  maxSimultaneousBookings: string;
  weeklySchedule: WeeklyScheduleForm;
  activeDay: Weekday;
}

interface BookingOnboardingForm {
  // Bot basics
  timeZone: string;

  // Booking rules
  bookingMinLeadHours: string;
  bookingMaxAdvanceDays: string;
  bookingReminderWindowHours: string;
  bookingReminderMinLeadHours: string;

  // Services
  services: ServiceForm[];
}

const buildEmptyWeeklySchedule = (): WeeklyScheduleForm => {
  const schedule: Partial<WeeklyScheduleForm> = {};
  WEEKDAYS_ORDER.forEach((day) => {
    schedule[day] = {
      enabled: false,
      windows: [{ start: "", end: "" }]
    };
  });
  return schedule as WeeklyScheduleForm;
};

const fromApiSchedule = (
  apiSchedule: BookingWeeklySchedule | null | undefined
): WeeklyScheduleForm => {
  const base = buildEmptyWeeklySchedule();

  if (!apiSchedule) {
    return base;
  }

  WEEKDAYS_ORDER.forEach((day) => {
    const windows = apiSchedule[day];
    if (windows && windows.length > 0) {
      base[day] = {
        enabled: true,
        windows: windows.map((w) => ({
          start: w.start,
          end: w.end
        }))
      };
    }
  });

  return base;
};

const normalizeWeeklyScheduleForApi = (
  formSchedule: WeeklyScheduleForm
): BookingWeeklySchedule | null => {
  const result: BookingWeeklySchedule = {};

  WEEKDAYS_ORDER.forEach((day) => {
    const dayForm = formSchedule[day];
    if (!dayForm.enabled) {
      return;
    }

    const validWindows: OpeningWindowForm[] = [];
    dayForm.windows.forEach((w) => {
      const start = w.start.trim();
      const end = w.end.trim();
      if (!start || !end) return;
      if (start >= end) return; // ignore invalid ranges
      validWindows.push({ start, end });
    });

    if (validWindows.length > 0) {
      result[day] = validWindows.map((w) => ({
        start: w.start,
        end: w.end
      }));
    }
  });

  return Object.keys(result).length > 0 ? result : null;
};

// For fields that MUST be a number when bookings are enabled, with defaults
const parseIntWithDefault = (value: string, defaultValue: number): number => {
  const trimmed = value.trim();
  if (!trimmed) return defaultValue;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return defaultValue;
  return Math.floor(parsed);
};

// For optional positive ints (we allow null)
const parseOptionalPositiveInt = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.floor(parsed);
};

const OnboardingBookingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState<BookingOnboardingForm | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // master feature toggle
  const [bookingEnabled, setBookingEnabled] = useState<boolean>(false);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    getBotById(id)
      .then((botData) => {
        const toStringOrEmpty = (value: number | null | undefined): string =>
          typeof value === "number" ? String(value) : "";

        const servicesFromApi = Array.isArray(botData.bookingServices)
          ? botData.bookingServices
          : [];

        const services: ServiceForm[] =
          servicesFromApi.length > 0
            ? servicesFromApi.map((service) => {
                const schedule = fromApiSchedule(service.weeklySchedule ?? null);
                const activeDay =
                  WEEKDAYS_ORDER.find((d) => schedule[d].enabled) ?? "monday";

                return {
                  name: service.name || "",
                  aliases: (service.aliases || []).join(", "),
                  calendarId: service.calendarId || "",
                  durationMinutes:
                    typeof service.durationMinutes === "number"
                      ? String(service.durationMinutes)
                      : "30",
                  maxSimultaneousBookings: toStringOrEmpty(
                    service.maxSimultaneousBookings
                  ),
                  weeklySchedule: schedule,
                  activeDay
                };
              })
            : [
                (() => {
                  const schedule = fromApiSchedule(
                    botData.bookingWeeklySchedule ?? null
                  );
                  const activeDay =
                    WEEKDAYS_ORDER.find((d) => schedule[d].enabled) ?? "monday";

                  return {
                    name: "General",
                    aliases: "",
                    calendarId: botData.calendarId || "",
                    durationMinutes: botData.defaultDurationMinutes
                      ? String(botData.defaultDurationMinutes)
                      : "30",
                    maxSimultaneousBookings: toStringOrEmpty(
                      botData.bookingMaxSimultaneousBookings
                    ),
                    weeklySchedule: schedule,
                    activeDay
                  };
                })()
              ];

        setForm({
          timeZone: botData.timeZone || "",
          bookingMinLeadHours: toStringOrEmpty(botData.bookingMinLeadHours),
          bookingMaxAdvanceDays: toStringOrEmpty(
            botData.bookingMaxAdvanceDays
          ),
          bookingReminderWindowHours: toStringOrEmpty(
            botData.bookingReminderWindowHours ?? 1
          ),
          bookingReminderMinLeadHours: toStringOrEmpty(
            botData.bookingReminderMinLeadHours ?? 1
          ),
          services
        });

        // initialise toggle from bot flag
        setBookingEnabled(!!botData.useCalendar);
      })
      .catch((err: any) => {
        console.error(err);
        setError(err?.message || t("botSettings.errors.loadFailed"));
      })
      .finally(() => setLoading(false));
  }, [id, t]);

  const handleStringChange =
    (field: keyof BookingOnboardingForm) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!form) return;
      setForm({ ...form, [field]: e.target.value });
    };

  const handleServiceChange =
    (index: number, field: keyof ServiceForm) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!form) return;
      const services = [...form.services];
      services[index] = { ...services[index], [field]: e.target.value };
      setForm({ ...form, services });
    };

  const handleServiceDayEnabledChange = (
    serviceIndex: number,
    day: Weekday,
    enabled: boolean
  ): void => {
    if (!form) return;
    const services = [...form.services];
    const service = services[serviceIndex];
    services[serviceIndex] = {
      ...service,
      weeklySchedule: {
        ...service.weeklySchedule,
        [day]: {
          ...service.weeklySchedule[day],
          enabled
        }
      }
    };
    setForm({ ...form, services });
  };

  const handleServiceWindowChange = (
    serviceIndex: number,
    day: Weekday,
    index: number,
    field: "start" | "end",
    value: string
  ): void => {
    if (!form) return;
    const services = [...form.services];
    const service = services[serviceIndex];
    const daySchedule = service.weeklySchedule[day];
    const windows = [...daySchedule.windows];
    windows[index] = {
      ...windows[index],
      [field]: value
    };
    services[serviceIndex] = {
      ...service,
      weeklySchedule: {
        ...service.weeklySchedule,
        [day]: {
          ...daySchedule,
          windows
        }
      }
    };
    setForm({ ...form, services });
  };

  const handleServiceAddWindow = (
    serviceIndex: number,
    day: Weekday
  ): void => {
    if (!form) return;
    const services = [...form.services];
    const service = services[serviceIndex];
    const daySchedule = service.weeklySchedule[day];
    services[serviceIndex] = {
      ...service,
      weeklySchedule: {
        ...service.weeklySchedule,
        [day]: {
          ...daySchedule,
          windows: [...daySchedule.windows, { start: "", end: "" }]
        }
      }
    };
    setForm({ ...form, services });
  };

  const handleServiceRemoveWindow = (
    serviceIndex: number,
    day: Weekday,
    index: number
  ): void => {
    if (!form) return;
    const services = [...form.services];
    const service = services[serviceIndex];
    const daySchedule = service.weeklySchedule[day];
    const windows = daySchedule.windows.filter((_, i) => i !== index);
    services[serviceIndex] = {
      ...service,
      weeklySchedule: {
        ...service.weeklySchedule,
        [day]: {
          ...daySchedule,
          windows: windows.length > 0 ? windows : [{ start: "", end: "" }]
        }
      }
    };
    setForm({ ...form, services });
  };

  const handleServiceActiveDayChange = (
    serviceIndex: number,
    day: Weekday
  ): void => {
    if (!form) return;
    const services = [...form.services];
    services[serviceIndex] = { ...services[serviceIndex], activeDay: day };
    setForm({ ...form, services });
  };

  const handleAddService = (): void => {
    if (!form) return;
    const services = [...form.services];
    if (services.length === 1 && !services[0].name.trim()) {
      services[0] = { ...services[0], name: "General" };
    }
    const newService: ServiceForm = {
      name: "",
      aliases: "",
      calendarId: "",
      durationMinutes: "30",
      maxSimultaneousBookings: "",
      weeklySchedule: buildEmptyWeeklySchedule(),
      activeDay: "monday"
    };
    setForm({ ...form, services: [...services, newService] });
  };

  const handleRemoveService = (index: number): void => {
    if (!form) return;
    const services = form.services.filter((_, i) => i != index);
    setForm({ ...form, services: services.length ? services : [] });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    if (!id || !form) return;
    if (!bookingEnabled) {
      // Guard: shouldn't happen because button is disabled, but just in case
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const isMultiService = form.services.length > 1;
      const bookingServices = form.services
        .map((service, index) => {
          const name = service.name.trim();
          const calendarId = service.calendarId.trim();
          if (!calendarId) return null;

          if (isMultiService && !name) {
            return null;
          }

          const durationMinutes = parseIntWithDefault(
            service.durationMinutes,
            30
          );
          const weeklySchedule = normalizeWeeklyScheduleForApi(
            service.weeklySchedule
          );
          const aliases = service.aliases
            .split(",")
            .map((alias) => alias.trim())
            .filter((alias) => alias.length > 0);

          return {
            name: name || "General",
            aliases,
            calendarId,
            durationMinutes,
            maxSimultaneousBookings: parseOptionalPositiveInt(
              service.maxSimultaneousBookings
            ),
            weeklySchedule
          };
        })
        .filter(Boolean) as Array<{
        name: string;
        aliases: string[];
        calendarId: string;
        durationMinutes: number;
        maxSimultaneousBookings: number | null;
        weeklySchedule: BookingWeeklySchedule | null;
      }>;

      if (isMultiService && bookingServices.length !== form.services.length) {
        setError(
          t(
            "botSettings.onboarding.booking.serviceNameRequired",
            "Service name is required when you add multiple services."
          )
        );
        return;
      }
      if (bookingServices.length === 0) {
        setError(
          t(
            "botSettings.onboarding.booking.servicesRequired",
            "Add at least one service with a calendar before continuing."
          )
        );
        return;
      }

      // These two must never be null when bookings are enabled.
      const bookingMinLeadHours = parseIntWithDefault(
        form.bookingMinLeadHours,
        3
      ); // 3 hours by default
      const bookingMaxAdvanceDays = parseIntWithDefault(
        form.bookingMaxAdvanceDays,
        30
      ); // 30 days by default

      const primary = bookingServices[0];

      await updateBot(id, {
        timeZone: form.timeZone.trim() || null,
        bookingMinLeadHours,
        bookingMaxAdvanceDays,
        bookingReminderWindowHours: parseIntWithDefault(
          form.bookingReminderWindowHours,
          1
        ),
        bookingReminderMinLeadHours: parseIntWithDefault(
          form.bookingReminderMinLeadHours,
          1
        ),
        bookingServices,
        // legacy fallback from first service
        calendarId: primary?.calendarId || null,
        defaultDurationMinutes: primary?.durationMinutes || 30,
        bookingMaxSimultaneousBookings:
          primary?.maxSimultaneousBookings ?? null,
        bookingWeeklySchedule: primary?.weeklySchedule ?? null,
        // feature flag
        useCalendar: bookingEnabled,
        // keep always-on features
        ...ALWAYS_ON_FEATURES
      });

      navigate(`/onboarding/bots/${encodeURIComponent(id)}/lead-ads`);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botSettings.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    if (!id) return;
    setSaving(true);
    setError(null);
    try {
      await updateBot(id, {
        ...ALWAYS_ON_FEATURES,
        useCalendar: false
      });
      navigate(`/onboarding/bots/${encodeURIComponent(id)}/lead-ads`);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("common.error", "Something went wrong"));
    } finally {
      setSaving(false);
    }
  };

  const renderContent = () => {
    if (!id) {
      return (
        <p className="muted">
          {t("botSettings.missingId", "Missing bot id in URL.")}
        </p>
      );
    }

    if (loading || !form) {
      return <p className="muted">{t("botSettings.loading", "Loading…")}</p>;
    }

    const isMultiService = form.services.length > 1;

    return (
      <form className="form booking-form" onSubmit={handleSubmit}>
        {/* Skip top-right */}
        <div className="onboarding-section-header">
          <button
            type="button"
            className="btn-secondary btn-color"
            onClick={handleSkip}
            disabled={saving}
          >
            {t(
              "botSettings.onboarding.common.skip",
              "Skip for now"
            )}
          </button>
        </div>

        {error && <div className="form-error onboarding-form-error-txt">{error}</div>}

        {/* master bookings toggle */}
        <div className="booking-enable-row">
          <div>
            <h2 className="booking-section-title">
              {t(
                "botSettings.onboarding.booking.toggleTitle",
                "Let Coslo propose and book meetings"
              )}
            </h2>
            <p className="booking-section-subtitle">
              {t(
                "botSettings.onboarding.booking.toggleSubtitle",
                "When enabled, Coslo will check your calendar and offer time slots directly in chat."
              )}
            </p>
          </div>
          <div className="booking-enable-toggle">
            <label className="booking-toggle">
              <input
                type="checkbox"
                checked={bookingEnabled}
                onChange={(e) => setBookingEnabled(e.target.checked)}
              />
              <span className="booking-toggle-slider" />
              <span className="booking-toggle-label">
                {bookingEnabled
                  ? t(
                      "botSettings.onboarding.booking.toggleOn",
                      "Bookings are ON"
                    )
                  : t(
                      "botSettings.onboarding.booking.toggleOff",
                      "Bookings are OFF"
                    )}
              </span>
            </label>
            {!bookingEnabled && (
              <p className="muted small">
                {t(
                  "botSettings.onboarding.booking.toggleOffHint",
                  "You can keep bookings disabled for now and still complete onboarding. You’ll be able to turn them on later from the app."
                )}
              </p>
            )}
          </div>
        </div>

        {/* When OFF, just explanation + nav, NEXT disabled */}
        {!bookingEnabled && (
          <div className="form-actions booking-actions">
            <button
              className="btn-secondary btn-color"
              type="button"
              onClick={handleBack}
              disabled={saving}
            >
              {t("common.back", "Back")}
            </button>
            <button
              className="btn-primary"
              type="button"
              disabled={true}
              title={t(
                "botSettings.onboarding.booking.enableToContinue",
                "Turn on bookings above or skip this step to continue."
              )}
            >
              {t(
                "botSettings.onboarding.booking.nextWhenDisabled",
                "Continue to Lead Ads"
              )}
            </button>
          </div>
        )}

        {/* When ON, show the full configuration UI and a real submit button */}
        {bookingEnabled && (
          <>
            {/* Top grid: calendar basics + rules */}
            <div className="booking-grid">
              <section className="booking-section">
                <h2 className="booking-section-title">
                  {t(
                    "botSettings.sections.timezone.title",
                    "Business time zone"
                  )}
                </h2>
                <p className="booking-section-subtitle">
                  {t(
                    "botSettings.sections.timezone.hint",
                    "Used to validate booking times across all services."
                  )}
                </p>

                <div className="booking-fields-grid">
                  <label className="form-field">
                    <span>{t("botSettings.fields.timeZone.label")}</span>
                    <input
                      type="text"
                      value={form.timeZone}
                      onChange={handleStringChange("timeZone")}
                      placeholder={t(
                        "botSettings.fields.timeZone.placeholder",
                        "e.g. Europe/Rome"
                      )}
                    />
                    <span className="bot-settings-help">
                      {t("botSettings.fields.timeZone.help")}
                    </span>
                  </label>
                </div>
              </section>

              <section className="booking-section">
                <h2 className="booking-section-title">
                  {t(
                    "botSettings.sections.bookingRules.title",
                    "Booking rules"
                  )}
                </h2>
                <p className="booking-section-subtitle">
                  {t(
                    "botSettings.sections.bookingRules.hint",
                    "Control how far in advance users can book."
                  )}
                </p>

                <div className="booking-fields-grid">
                  <label className="form-field">
                    <span>
                      {t("botSettings.fields.bookingMinLeadHours.label")}
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={form.bookingMinLeadHours}
                      onChange={handleStringChange("bookingMinLeadHours")}
                      placeholder={t(
                        "botSettings.fields.bookingMinLeadHours.placeholder"
                      )}
                    />
                    <span className="bot-settings-help">
                      {t("botSettings.fields.bookingMinLeadHours.help")}
                    </span>
                  </label>

                  <label className="form-field">
                    <span>
                      {t("botSettings.fields.bookingMaxAdvanceDays.label")}
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={form.bookingMaxAdvanceDays}
                      onChange={handleStringChange("bookingMaxAdvanceDays")}
                      placeholder={t(
                        "botSettings.fields.bookingMaxAdvanceDays.placeholder"
                      )}
                    />
                    <span className="bot-settings-help">
                      {t("botSettings.fields.bookingMaxAdvanceDays.help")}
                    </span>
                  </label>

                </div>
              </section>
            </div>

                        {/* Services */}
            <section className="booking-section booking-availability">
              <h2 className="booking-section-title">
                {t(
                  "botSettings.sections.services.title",
                  "Services & calendars"
                )}
              </h2>
              <p className="booking-section-subtitle">
                {t(
                  "botSettings.sections.services.hint",
                  "Create one service per calendar and set its duration, capacity, and opening hours."
                )}
              </p>

              {form.services.map((service, serviceIndex) => (
                <div key={serviceIndex} className="booking-day-detail-card" style={{ marginBottom: "1.2rem" }}>
                  <div className="booking-day-detail-header">
                    <div>
                      <h3 className="booking-day-detail-title">
                        {service.name || t("botSettings.sections.services.unnamed", "Service")}
                      </h3>
                      <p className="booking-day-detail-subtitle">
                        {t(
                          "botSettings.sections.services.cardHint",
                          "Configure the calendar and availability for this service."
                        )}
                      </p>
                    </div>
                    {form.services.length > 1 && (
                      <button
                        type="button"
                        className="btn-link booking-remove-range"
                        onClick={() => handleRemoveService(serviceIndex)}
                      >
                        {t("botSettings.sections.services.remove", "Remove service")}
                      </button>
                    )}
                  </div>

                  <div className="booking-fields-grid" style={{ marginBottom: "1rem" }}>
                    <label className="form-field">
                      <span>
                        {isMultiService
                          ? t("botSettings.services.fields.name", "Service name")
                          : t("botSettings.services.fields.labelOptional", "Label (optional)")}
                      </span>
                      <input
                        type="text"
                        value={service.name}
                        onChange={handleServiceChange(serviceIndex, "name")}
                        placeholder={t(
                          isMultiService
                            ? "botSettings.services.fields.namePlaceholder"
                            : "botSettings.services.fields.labelPlaceholder",
                          isMultiService ? "e.g. Haircut" : "e.g. General"
                        )}
                      />
                    </label>

                    <label className="form-field">
                      <span>
                        {t("botSettings.services.fields.aliases", "Aliases (optional)")}
                      </span>
                      <input
                        type="text"
                        value={service.aliases}
                        onChange={handleServiceChange(serviceIndex, "aliases")}
                        placeholder={t(
                          "botSettings.services.fields.aliasesPlaceholder",
                          "e.g. womens haircut, haircut for women"
                        )}
                      />
                    </label>

                    <label className="form-field">
                      <span>{t("botSettings.fields.calendarId.label")}</span>
                      <input
                        type="text"
                        value={service.calendarId}
                        onChange={handleServiceChange(serviceIndex, "calendarId")}
                        placeholder={t(
                          "botSettings.fields.calendarId.placeholder",
                          "e.g. primary or a specific calendar id"
                        )}
                      />
                      <span className="bot-settings-help">
                        {t("botSettings.fields.calendarId.help")} <code>primary</code>
                      </span>
                    </label>

                    <label className="form-field">
                      <span>
                        {t(
                          "botSettings.services.fields.duration",
                          "Duration (minutes)"
                        )}
                      </span>
                      <input
                        type="number"
                        min={5}
                        max={480}
                        value={service.durationMinutes}
                        onChange={handleServiceChange(
                          serviceIndex,
                          "durationMinutes"
                        )}
                        placeholder={t(
                          "botSettings.fields.defaultDurationMinutes.placeholder",
                          "30"
                        )}
                      />
                    </label>

                    <label className="form-field">
                      <span>
                        {t(
                          "botSettings.fields.bookingMaxSimultaneousBookings.label"
                        )}
                      </span>
                      <input
                        type="number"
                        min={1}
                        value={service.maxSimultaneousBookings}
                        onChange={handleServiceChange(
                          serviceIndex,
                          "maxSimultaneousBookings"
                        )}
                        placeholder={t(
                          "botSettings.fields.bookingMaxSimultaneousBookings.placeholder"
                        )}
                      />
                      <span className="bot-settings-help">
                        {t(
                          "botSettings.fields.bookingMaxSimultaneousBookings.help"
                        )}
                      </span>
                    </label>
                  </div>

                  <div className="booking-day-tabs">
                    {WEEKDAYS_ORDER.map((dayKey) => {
                      const dayForm = service.weeklySchedule[dayKey];
                      const isActive = service.activeDay === dayKey;
                      const isEnabled = dayForm.enabled;

                      const className = [
                        "booking-day-pill",
                        isActive && "booking-day-pill--active"
                      ]
                        .filter(Boolean)
                        .join(" ");

                      return (
                        <button
                          key={`${serviceIndex}-${dayKey}`}
                          type="button"
                          className={className}
                          onClick={() =>
                            handleServiceActiveDayChange(serviceIndex, dayKey)
                          }
                        >
                          <span className="booking-day-pill-label">
                            {t(`botSettings.weekdays.${dayKey}`)}
                          </span>
                          <span
                            className={
                              "booking-day-pill-dot" +
                              (isEnabled ? " booking-day-pill-dot--on" : "")
                            }
                            aria-hidden="true"
                          />
                        </button>
                      );
                    })}
                  </div>

                  {(() => {
                    const day = service.activeDay;
                    const dayForm = service.weeklySchedule[day];

                    return (
                      <div className="booking-day-detail-card">
                        <div className="booking-day-detail-header">
                          <div>
                            <h3 className="booking-day-detail-title">
                              {t(`botSettings.weekdays.${day}`)}
                            </h3>
                            <p className="booking-day-detail-subtitle">
                              {dayForm.enabled
                                ? t(
                                    "botSettings.sections.availability.enabledHint",
                                    "Bookings are allowed on this day within the time ranges below."
                                  )
                                : t(
                                    "botSettings.sections.availability.disabledHint",
                                    "Bookings are not allowed on this day until you enable it."
                                  )}
                            </p>
                          </div>

                          <label className="booking-toggle">
                            <input
                              type="checkbox"
                              checked={dayForm.enabled}
                              onChange={(e) =>
                                handleServiceDayEnabledChange(
                                  serviceIndex,
                                  day,
                                  e.target.checked
                                )
                              }
                            />
                            <span className="booking-toggle-slider" />
                            <span className="booking-toggle-label">
                              {t(
                                "botSettings.sections.availability.acceptBookings",
                                "Accept bookings this day"
                              )}
                            </span>
                          </label>
                        </div>

                        <div className="booking-day-ranges">
                          {dayForm.windows.map((window, idx) => (
                            <div
                              key={`${serviceIndex}-${day}-${idx}`}
                              className="booking-day-range-row"
                            >
                              <div className="booking-day-range-inputs">
                                <label>
                                  <span className="booking-range-label">
                                    {t(
                                      "botSettings.sections.availability.from",
                                      "From"
                                    )}
                                  </span>
                                  <input
                                    type="time"
                                    value={window.start}
                                    onChange={(e) =>
                                      handleServiceWindowChange(
                                        serviceIndex,
                                        day,
                                        idx,
                                        "start",
                                        e.target.value
                                      )
                                    }
                                  />
                                </label>

                                <label>
                                  <span className="booking-range-label">
                                    {t(
                                      "botSettings.sections.availability.to",
                                      "To"
                                    )}
                                  </span>
                                  <input
                                    type="time"
                                    value={window.end}
                                    onChange={(e) =>
                                      handleServiceWindowChange(
                                        serviceIndex,
                                        day,
                                        idx,
                                        "end",
                                        e.target.value
                                      )
                                    }
                                  />
                                </label>
                              </div>

                              {idx > 0 && (
                                <button
                                  type="button"
                                  className="btn-link booking-remove-range"
                                  onClick={() =>
                                    handleServiceRemoveWindow(
                                      serviceIndex,
                                      day,
                                      idx
                                    )
                                  }
                                >
                                  {t(
                                    "botSettings.sections.availability.removeRange",
                                    "Remove range"
                                  )}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        <button
                          type="button"
                          className="btn-secondary btn-xs booking-add-range btn-color"
                          onClick={() =>
                            handleServiceAddWindow(serviceIndex, day)
                          }
                        >
                          {t(
                            "botSettings.sections.availability.addRange",
                            "Add time range"
                          )}
                        </button>

                        <p className="bot-settings-hint booking-availability-note">
                          {t("botSettings.sections.availability.note")}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              ))}

              <button
                type="button"
                className="btn-secondary btn-xs booking-add-range btn-color"
                onClick={handleAddService}
              >
                {t("botSettings.sections.services.add", "Add service")}
              </button>
            </section>

            <div className="form-actions booking-actions">
              <button
                className="btn-secondary btn-color"
                type="button"
                onClick={handleBack}
                disabled={saving}
              >
                {t("common.back", "Back")}
              </button>
              <button className="btn-primary" type="submit" disabled={saving}>
                {saving
                  ? t("botSettings.actions.saving", "Saving…")
                  : t(
                      "botSettings.onboarding.booking.next",
                      "Save & continue to Lead Ads"
                    )}
              </button>
            </div>
          </>
        )}
      </form>
    );
  };

  return (
    <OnboardingLayout
      currentStep="booking"
      botId={id}
      title={t(
        "botSettings.onboarding.booking.title",
        "Enable bookings from chat"
      )}
      subtitle={t(
        "botSettings.onboarding.booking.subtitle",
        "Connect your calendar, define your available slots and booking rules."
      )}
      layout="full"
    >
      <div className="onboarding-booking">{renderContent()}</div>
    </OnboardingLayout>
  );
};

export default OnboardingBookingPage;
